/**
 * Mode system — immersive / standard.
 *
 * The class on <html> is set pre-paint by an inline script in theme.liquid.
 * This module owns everything after that: the gate, the toggles, and the
 * ss:modechange event that the feature modules listen to.
 *
 * ── Why nothing imports this file ──────────────────────────────────────────
 * Shopify serves assets with a cache-busting query (`mode.js?v=17061…`), but a
 * bare `import './mode.js'` resolves without one. Those are two different URLs,
 * so the browser instantiates the module twice and every listener is attached
 * twice — the toggle flips to standard and straight back again.
 *
 * So: no module imports another. The feature modules each inline the two pure
 * helpers below (three lines, stateless), and this file stays the only owner of
 * state and listeners. The window guard is belt-and-braces.
 */

if (!window.__ssMode) {
  window.__ssMode = true;

  const KEY = 'ss_mode';
  const root = document.documentElement;

  const getMode = () =>
    root.classList.contains('mode-immersive') ? 'immersive' : 'standard';

  /** Immersive is only honoured on a fine pointer that hasn't asked for less motion. */
  const canBeImmersive = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    return fine && !reduce;
  };

  function setMode(next, { persist = true } = {}) {
    const mode = next === 'immersive' && canBeImmersive() ? 'immersive' : 'standard';

    root.classList.remove('mode-immersive', 'mode-standard', 'mode-undecided');
    root.classList.add(`mode-${mode}`);

    if (persist) {
      try {
        localStorage.setItem(KEY, mode);
      } catch (e) {
        /* private browsing — the session still works, it just won't be remembered */
      }
    }

    document.dispatchEvent(new CustomEvent('ss:modechange', { detail: { mode } }));
    return mode;
  }

  /* ---- Gate ----------------------------------------------- */

  const gate = document.getElementById('entry-gate');

  function closeGate() {
    if (!gate) return;
    gate.hidden = true;
    root.classList.remove('mode-undecided');
  }

  if (gate && root.classList.contains('mode-undecided')) {
    // Focus the first choice so keyboard visitors land inside the dialog.
    const first = gate.querySelector('[data-mode-choice]');
    if (first) first.focus({ preventScroll: true });

    // Trap focus for as long as the gate is up.
    gate.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setMode('standard');
        closeGate();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = gate.querySelectorAll('[data-mode-choice]');
      if (!focusable.length) return;
      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    });
  }

  /* ---- Choices and toggles --------------------------------- */

  document.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-mode-choice]');
    if (choice) {
      setMode(choice.dataset.modeChoice);
      closeGate();
      return;
    }

    const toggle = event.target.closest('[data-mode-toggle]');
    if (toggle) {
      event.preventDefault();
      const next = getMode() === 'immersive' ? 'standard' : 'immersive';
      const applied = setMode(next);

      // If immersive was refused (touch or reduced motion), say so rather than
      // appearing to do nothing.
      if (next === 'immersive' && applied !== 'immersive') {
        toggle.setAttribute('data-unavailable', 'true');
        window.setTimeout(() => toggle.removeAttribute('data-unavailable'), 2400);
      }
    }
  });

  /* A visitor who turns on reduced motion mid-session drops to standard,
     without overwriting the preference they chose earlier. */
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (typeof reduceQuery.addEventListener === 'function') {
    reduceQuery.addEventListener('change', (event) => {
      if (event.matches && getMode() === 'immersive') setMode('standard', { persist: false });
    });
  }
}
